export const realEstatePracticeFull = [
  {
    title: "Explicación Detallada de los Campos del Contrato de Tokenización Inmobiliaria",
    value: `
En esta sección aprenderás a interpretar y rellenar **todos los campos fundamentales** del contrato de tokenización inmobiliaria. Cada punto se basa en el ejemplo de contrato que descargaste en PDF ([Real-estate+Asset+Contract+Example.pdf](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/Real-estate+Asset+Contract+Example.pdf)), y está pensado para que entiendas su función legal y práctica antes de migrarlos a tu smart contract en la blockchain.

---

### 1. ¿Cuál es el objetivo de esta práctica?
El objetivo es **aprender a interpretar, adaptar y rellenar un contrato de tokenización inmobiliaria**, extrayendo todos los datos clave del activo para llevarlos a la blockchain.  
Trabajarás con un caso real: una propiedad en renta, representada mediante tokens ERC20.  
Debes analizar cada campo, entender su función y preparar la información para migrarla a tu contrato inteligente.  
Esto asegura que tu tokenización esté respaldada legal, técnica y documentalmente.

---

### 2. ¿Cómo se describe el activo a tokenizar?
La **descripción del activo** debe ser clara y verificable.  
Ejemplo para esta práctica:
- Propiedad: **Casa de 4 habitaciones en renta**
- Incluye: **Enlace al plano arquitectónico**, ubicación precisa ([ver en Google Maps](https://maps.google.com)), y documentación registral.  
Estos datos permiten a cualquier interesado comprobar que el inmueble existe y cumple con los requisitos legales.

---

### 3. ¿Cómo se determina el ingreso anual proyectado?
Debes calcular y documentar los **ingresos estimados por la propiedad**.  
En este caso:
- **$2,000 CAD mensuales**
- **$24,000 CAD anuales**  
Esto muestra la rentabilidad potencial para los inversionistas y ayuda a definir el valor de los tokens.

---

### 4. ¿Qué es la prueba de título y por qué es importante?
La **prueba de título** demuestra la propiedad legal del inmueble.  
Incluye:
- **Número de registro** de la escritura
- **Enlace al registro público** (o copia digital)  
Así, se garantiza que el activo está debidamente registrado y no hay dudas sobre su legitimidad.

---

### 5. ¿Por qué incluir el certificado de seguro?
El **certificado de seguro** acredita que la propiedad está protegida frente a riesgos.  
Debes agregar:
- **Nombre de la aseguradora**
- **Número de póliza**  
Esto ofrece confianza adicional a inversionistas, ya que el inmueble está respaldado por una póliza vigente.

---

### 6. ¿Qué datos personales y de compraventa se documentan?
Incluye información del propietario:
- **Nombre completo**
- **Dirección**
- **Precio de compra original** (verificable en la escritura)

Y el acuerdo de compraventa:
- **Enlace al contrato o documento**  
Esto respalda la transparencia de la operación y la procedencia del inmueble.

---

### 7. ¿Cómo se determina el valor actual de la propiedad?
Debes usar una **tasación reciente** para fijar el valor de referencia.  
En este caso:
- **$1,000,000 CAD** (al 1 de noviembre de 2018)
- Adjunta el **certificado de tasación** o un enlace para consulta.  
Esto ayuda a definir el precio de los tokens y la valuación del activo tokenizado.

---

### 8. ¿Por qué añadir un perfil social del propietario?
Un **perfil social** (como LinkedIn, página web, empresa) **aumenta la confianza** y permite verificar que hay una persona real detrás del proyecto.  
Es fundamental para dar transparencia y credibilidad a la tokenización.

---

### 9. ¿Para qué sirve el certificado notariado?
El **certificado notariado** es un documento oficial que **valida toda la información del contrato** y los anexos presentados.  
Agrega el enlace o copia digital del certificado para que cualquier usuario pueda auditar la documentación.

---

### 10. ¿Cómo se define el nombre y la estructura del token?
Debes asignar un **nombre único al token** (ejemplo: *MyRealToken*) y definir:
- Proporción de activo que representa cada token (ejemplo: 0.0001%)
- Precio inicial del token (ejemplo: 300 tokens = 1 ETH ≈ $1 CAD/token)
- Cantidad total de tokens emitidos (ejemplo: 1,000,000)  
Esto estructura la emisión del token y su equivalencia respecto al activo real.

---

### 11. ¿Qué reglas sobre dividendos y derechos de voto se establecen?
Debes indicar claramente:
- **Dividendos:** Si los tokens otorgan (o no) derecho a recibir utilidades periódicas.
- **Derechos de voto:** Si los poseedores pueden participar en decisiones sobre la propiedad.  
En este ejemplo, **no aplican dividendos ni derechos de voto**.

---

### 12. ¿Por qué es importante la fecha y la firma del contrato?
La **fecha de creación** del contrato marca el momento legal de la emisión.  
La **firma del propietario** da validez legal al documento y a la tokenización.  
Ambos son requisitos para asegurar la legitimidad del proceso en la blockchain.

---

> **Recuerda:**  
Tener toda esta información bien documentada es el **primer paso para una tokenización profesional y segura**. Cada dato te ayudará a completar correctamente el smart contract ERC20 y facilitará auditorías, inversiones y confianza en tu proyecto.
`
  }
];

export default realEstatePracticeFull;
